import { Preferences } from "@capacitor/preferences";
import { createAuthClient } from "better-auth/react";
import { env } from "../../../env";

if (!env.nestApiUrl) {
  throw new Error("VITE_NEST_API_URL is not defined");
}

const AuthTokenStorageKey = "core-auth-bearer-token";

const getStoredAuthToken = async (): Promise<string | undefined> => {
  const { value } = await Preferences.get({ key: AuthTokenStorageKey });
  return value ?? undefined;
};

const setStoredAuthToken = async (token: string): Promise<void> => {
  await Preferences.set({ key: AuthTokenStorageKey, value: token });
};

const clearStoredAuthToken = async (): Promise<void> => {
  await Preferences.remove({ key: AuthTokenStorageKey });
};

const rawAuthClient = createAuthClient({
  baseURL: env.nestApiUrl,
  fetchOptions: {
    throw: true,
    auth: {
      type: "Bearer",
      token: getStoredAuthToken,
    },
    onSuccess: async (ctx) => {
      const authToken = ctx.response.headers.get("set-auth-token");
      if (authToken) {
        await setStoredAuthToken(authToken);
      }
    },
  },
});

const useIsLoggedIn = () => {
  const session = rawAuthClient.useSession();
  return !!session.data?.user;
};

export const authClient = {
  useSession: rawAuthClient.useSession,
  useIsLoggedIn,
  getSession: rawAuthClient.getSession,
  signUp: rawAuthClient.signUp,
  sendVerificationEmail: rawAuthClient.sendVerificationEmail,
  requestPasswordReset: rawAuthClient.requestPasswordReset,
  resetPassword: rawAuthClient.resetPassword,
  verifyEmail: rawAuthClient.verifyEmail,
  signIn: {
    email: (...args: Parameters<typeof rawAuthClient.signIn.email>) =>
      rawAuthClient.signIn.email(...args),
  },
  signOut: async (...args: Parameters<typeof rawAuthClient.signOut>) => {
    try {
      return await rawAuthClient.signOut(...args);
    } finally {
      await clearStoredAuthToken();
    }
  },
  getAuthToken: getStoredAuthToken,
};
